import { Eye, EyeOff, LoaderCircle, X } from 'lucide-react'
import { useState } from 'react'

import { supabase } from '../../lib/supabase'

function CambiarClaveUsuarioModal({
  relacion,
  empresaId,
  onClose,
  onGuardado,
}) {
  const [nuevaPassword, setNuevaPassword] = useState('')
  const [confirmarPassword, setConfirmarPassword] = useState('')
  const [mostrarPassword, setMostrarPassword] = useState(false)
  const [mostrarConfirmarPassword, setMostrarConfirmarPassword] =
    useState(false)
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState('')

  const cerrar = () => {
    if (guardando) return
    onClose()
  }

  const cambiarClave = async (event) => {
    event.preventDefault()

    if (guardando) return

    setError('')

    if (nuevaPassword.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.')
      return
    }

    if (nuevaPassword !== confirmarPassword) {
      setError('Las contraseñas no coinciden.')
      return
    }

    try {
      setGuardando(true)

      const { data, error: errorFuncion } =
        await supabase.functions.invoke('cambiar-clave-usuario', {
          body: {
            usuario_id: relacion.usuario_id,
            empresa_id: empresaId,
            nueva_password: nuevaPassword,
          },
        })

      if (errorFuncion) {
        throw errorFuncion
      }

      if (data?.error) {
        throw new Error(data.error)
      }

      onGuardado?.()
    } catch (errorCambiar) {
      console.error('Error al cambiar la contraseña:', errorCambiar)

      setError(
        errorCambiar.message ||
          'No fue posible actualizar la contraseña.'
      )
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          cerrar()
        }
      }}
    >
      <div className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-xl sm:p-8">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900">
              Cambiar contraseña
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              {relacion?.perfil?.nombre_completo}
              {relacion?.perfil?.correo
                ? ` — ${relacion.perfil.correo}`
                : ''}
            </p>
          </div>

          <button
            type="button"
            onClick={cerrar}
            disabled={guardando}
            aria-label="Cerrar"
            className="shrink-0 text-slate-400 transition hover:text-slate-600 disabled:cursor-not-allowed"
          >
            <X size={22} />
          </button>
        </div>

        <form
          className="space-y-5"
          onSubmit={cambiarClave}
        >
          <div>
            <label
              htmlFor="nueva_password"
              className="mb-2 block text-sm font-semibold text-slate-700"
            >
              Nueva contraseña
              <span className="ml-1 text-red-500">*</span>
            </label>

            <div className="relative">
              <input
                id="nueva_password"
                type={mostrarPassword ? 'text' : 'password'}
                value={nuevaPassword}
                onChange={(event) =>
                  setNuevaPassword(event.target.value)
                }
                placeholder="Mínimo 8 caracteres"
                disabled={guardando}
                className={`${estiloInput} pr-11`}
              />

              <button
                type="button"
                onClick={() => setMostrarPassword((valor) => !valor)}
                disabled={guardando}
                aria-label={
                  mostrarPassword
                    ? 'Ocultar contraseña'
                    : 'Mostrar contraseña'
                }
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-600 disabled:cursor-not-allowed"
              >
                {mostrarPassword ? (
                  <EyeOff size={18} />
                ) : (
                  <Eye size={18} />
                )}
              </button>
            </div>
          </div>

          <div>
            <label
              htmlFor="confirmar_nueva_password"
              className="mb-2 block text-sm font-semibold text-slate-700"
            >
              Confirmar contraseña
              <span className="ml-1 text-red-500">*</span>
            </label>

            <div className="relative">
              <input
                id="confirmar_nueva_password"
                type={mostrarConfirmarPassword ? 'text' : 'password'}
                value={confirmarPassword}
                onChange={(event) =>
                  setConfirmarPassword(event.target.value)
                }
                placeholder="Repite la contraseña"
                disabled={guardando}
                className={`${estiloInput} pr-11`}
              />

              <button
                type="button"
                onClick={() =>
                  setMostrarConfirmarPassword((valor) => !valor)
                }
                disabled={guardando}
                aria-label={
                  mostrarConfirmarPassword
                    ? 'Ocultar contraseña'
                    : 'Mostrar contraseña'
                }
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-600 disabled:cursor-not-allowed"
              >
                {mostrarConfirmarPassword ? (
                  <EyeOff size={18} />
                ) : (
                  <Eye size={18} />
                )}
              </button>
            </div>
          </div>

          {error && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              {error}
            </div>
          )}

          <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={cerrar}
              disabled={guardando}
              className="w-full rounded-lg border border-slate-300 px-5 py-2.5 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={guardando}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-400 sm:w-auto"
            >
              {guardando && (
                <LoaderCircle
                  size={18}
                  className="animate-spin"
                />
              )}

              {guardando ? 'Guardando...' : 'Cambiar contraseña'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const estiloInput =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100'

export default CambiarClaveUsuarioModal
